import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { setCookie } from 'helpers/cookie';
import type { IFavorite, ISelection } from 'old-store/types/gallery';
import type { ISelectionSaveValues } from 'modules/ScrAppBookNew/store/selectionWorkflow/actions';

// A guest's save refused because the email has an account; it runs again after the sign-in.
export interface IPendingLoginSave {
  values: ISelectionSaveValues;
  // true: the plain "Save" (update endpoint), false: "Send" (save endpoint)
  update: boolean;
}

export interface ISelectionEditDraft {
  sourceSelectionId: string;
  imageIds: string[];
  originalImageIds: string[];
  previousLikes: string[];
  previousSelectionStarted: boolean;
  previousMobileSelectionMode: boolean;
  sourceWasCurrent: boolean;
  saveModalOpen: boolean;
  // 'closed': the source was sent to the photographer meanwhile, so it can only be saved as new
  status: 'editing' | 'saving' | 'failed' | 'closed';
}

export interface INewSelectionState {
  all: ISelection[];
  allUserSelections: ISelection[];
  selectionStarted: boolean;
  mobileSelectionMode: boolean;
  updateSelectionState: boolean;
  likes: string[];
  currentSelection: ISelection;
  editDraft: ISelectionEditDraft | null;
  shareStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  pendingLoginSave: IPendingLoginSave | null;
}

const emptySelection = (): ISelection => ({
  __v: 0,
  _collection: '',
  _endCustomer: '',
  _id: '',
  _user: '',
  name: '',
  createdAt: '',
  updatedAt: '',
  completed: false,
  hasImagesFromHiddenGalleries: false,
  favorites: []
});

export const initialNewSelectionState: INewSelectionState = {
  all: [],
  allUserSelections: [],
  selectionStarted: false,
  mobileSelectionMode: false,
  updateSelectionState: false,
  likes: [],
  currentSelection: emptySelection(),
  editDraft: null,
  shareStatus: 'idle',
  pendingLoginSave: null
};

const setSelectionStartedCookie = (value: boolean) => {
  setCookie('selectionStarted', String(value));
};

const finishSelectionState = (state: INewSelectionState) => {
  state.selectionStarted = false;
  state.mobileSelectionMode = false;
  state.updateSelectionState = false;
  state.likes = [];
  state.all = state.all.map((selection) => ({ ...selection, current: false }));
  state.currentSelection = emptySelection();
  state.editDraft = null;
  setSelectionStartedCookie(false);
};

const newSelectionSlice = createSlice({
  name: 'scrAppBookNew/selection',
  initialState: initialNewSelectionState,
  reducers: {
    resetNewSelection: () => initialNewSelectionState,
    hydrateNewSelection(
      state,
      { payload }: PayloadAction<{ selections: ISelection[]; allUserSelections?: ISelection[] }>
    ) {
      state.all = payload.selections || [];
      state.allUserSelections = payload.allUserSelections || [];

      const currentSelection = state.all.find((selection) => selection.current);

      if (currentSelection) {
        state.currentSelection = currentSelection;
        state.likes = currentSelection.favorites
          .filter((favorite: IFavorite) => favorite.like)
          .map((favorite: IFavorite) => favorite._image);
        state.selectionStarted =
          state.likes.length > 0 || !!currentSelection.hasImagesFromHiddenGalleries;
        state.mobileSelectionMode = true;
        setSelectionStartedCookie(state.selectionStarted);
      } else if (!state.editDraft) {
        state.currentSelection = emptySelection();
        state.likes = [];
        state.selectionStarted = false;
        state.mobileSelectionMode = false;
        setSelectionStartedCookie(false);
      }

      if (state.editDraft) {
        state.likes = state.editDraft.imageIds;
        state.selectionStarted = true;
        state.mobileSelectionMode = true;
        setSelectionStartedCookie(true);
      }
    },
    addSelection(state, { payload }: PayloadAction<ISelection>) {
      if (!payload) return;

      const existingIndex = state.all.findIndex((selection) => selection._id === payload._id);
      if (existingIndex >= 0) state.all[existingIndex] = payload;
      else state.all.push(payload);

      if (payload.current) state.currentSelection = payload;
    },
    removeSelection(state, { payload }: PayloadAction<string>) {
      state.all = state.all.filter((selection) => selection._id !== payload);
      state.allUserSelections = state.allUserSelections.filter(
        (selection) => selection._id !== payload
      );
      if (state.currentSelection._id === payload) state.currentSelection = emptySelection();
    },
    replaceSelection(state, { payload }: PayloadAction<ISelection>) {
      const index = state.all.findIndex((selection) => selection._id === payload._id);
      if (index >= 0) state.all[index] = payload;
      else state.all.push(payload);
      if (payload.current) state.currentSelection = payload;
    },
    handleLikes(state, { payload }: PayloadAction<string>) {
      state.selectionStarted = true;
      state.mobileSelectionMode = true;
      setSelectionStartedCookie(true);
      state.likes = state.likes.includes(payload)
        ? state.likes.filter((imageId) => imageId !== payload)
        : [...state.likes, payload];
    },
    addLikes(state, { payload }: PayloadAction<string[]>) {
      state.selectionStarted = true;
      state.mobileSelectionMode = true;
      setSelectionStartedCookie(true);
      state.likes = Array.from(new Set([...state.likes, ...payload]));
    },
    removeLikes(state, { payload }: PayloadAction<string[]>) {
      const removed = new Set(payload);
      state.likes = state.likes.filter((imageId) => !removed.has(imageId));
    },
    startSelection(state) {
      state.selectionStarted = true;
      state.mobileSelectionMode = true;
      setSelectionStartedCookie(true);
    },
    finishSelection(state) {
      finishSelectionState(state);
    },
    // Mirrors what the server does on payment, for a checkout that returns without a reload.
    finishPurchasedSelection(state, { payload }: PayloadAction<string>) {
      finishSelectionState(state);
      const markCompleted = (selection: ISelection) =>
        selection._id === payload ? { ...selection, completed: true } : selection;
      state.all = state.all.map(markCompleted);
      state.allUserSelections = state.allUserSelections.map(markCompleted);
    },
    enableMobileSelectionMode(state) {
      state.mobileSelectionMode = true;
    },
    disableMobileSelectionMode(state) {
      state.mobileSelectionMode = false;
    },
    enableUpdateSelectionState(state) {
      state.updateSelectionState = true;
    },
    disableUpdateSelectionState(state) {
      state.updateSelectionState = false;
    },
    beginSelectionEdit(
      state,
      {
        payload
      }: PayloadAction<{
        sourceSelectionId: string;
        imageIds: string[];
      }>
    ) {
      const imageIds = Array.from(new Set(payload.imageIds));

      state.editDraft = {
        sourceSelectionId: payload.sourceSelectionId,
        imageIds,
        originalImageIds: [...imageIds],
        previousLikes: [...state.likes],
        previousSelectionStarted: state.selectionStarted,
        previousMobileSelectionMode: state.mobileSelectionMode,
        sourceWasCurrent: state.all.some(
          (selection) => selection._id === payload.sourceSelectionId && selection.current
        ),
        saveModalOpen: false,
        status: 'editing'
      };
      state.likes = imageIds;
      state.selectionStarted = true;
      state.mobileSelectionMode = true;
      setSelectionStartedCookie(true);
    },
    toggleSelectionEditImage(state, { payload }: PayloadAction<string>) {
      if (!state.editDraft) return;

      state.editDraft.imageIds = state.editDraft.imageIds.includes(payload)
        ? state.editDraft.imageIds.filter((imageId) => imageId !== payload)
        : [...state.editDraft.imageIds, payload];
      state.editDraft.status = 'editing';
      state.likes = state.editDraft.imageIds;
    },
    addSelectionEditImages(state, { payload }: PayloadAction<string[]>) {
      if (!state.editDraft) return;
      state.editDraft.imageIds = Array.from(new Set([...state.editDraft.imageIds, ...payload]));
      state.editDraft.status = 'editing';
      state.likes = state.editDraft.imageIds;
    },
    removeSelectionEditImages(state, { payload }: PayloadAction<string[]>) {
      if (!state.editDraft) return;
      const removed = new Set(payload);
      state.editDraft.imageIds = state.editDraft.imageIds.filter(
        (imageId) => !removed.has(imageId)
      );
      state.editDraft.status = 'editing';
      state.likes = state.editDraft.imageIds;
    },
    openSelectionEditSaveModal(state) {
      if (!state.editDraft) return;
      state.editDraft.saveModalOpen = true;
      state.editDraft.status = 'editing';
    },
    closeSelectionEditSaveModal(state) {
      if (!state.editDraft || state.editDraft.status === 'saving') return;
      state.editDraft.saveModalOpen = false;
      state.editDraft.status = 'editing';
    },
    setSelectionEditStatus(state, { payload }: PayloadAction<ISelectionEditDraft['status']>) {
      if (state.editDraft) state.editDraft.status = payload;
    },
    completeSelectionEdit(state, { payload }: PayloadAction<ISelection>) {
      if (!state.editDraft) return;

      const draft = state.editDraft;
      const sourceWasOverwritten =
        payload._id === draft.sourceSelectionId && draft.sourceWasCurrent;
      const likedImageIds = payload.favorites
        .filter((favorite) => favorite.like)
        .map((favorite) => favorite._image);

      const existingIndex = state.all.findIndex((selection) => selection._id === payload._id);
      if (existingIndex >= 0) state.all[existingIndex] = payload;
      else state.all.push(payload);

      state.likes = sourceWasOverwritten ? likedImageIds : draft.previousLikes;
      state.selectionStarted = sourceWasOverwritten
        ? likedImageIds.length > 0 || !!payload.hasImagesFromHiddenGalleries
        : draft.previousSelectionStarted;
      state.mobileSelectionMode = draft.previousMobileSelectionMode;
      if (sourceWasOverwritten) state.currentSelection = payload;
      state.editDraft = null;
      setSelectionStartedCookie(state.selectionStarted);
    },
    discardSelectionEdit(state) {
      if (!state.editDraft) return;
      state.likes = state.editDraft.previousLikes;
      state.selectionStarted = state.editDraft.previousSelectionStarted;
      state.mobileSelectionMode = state.editDraft.previousMobileSelectionMode;
      state.editDraft = null;
      setSelectionStartedCookie(state.selectionStarted);
    },
    setShareStatus(state, { payload }: PayloadAction<INewSelectionState['shareStatus']>) {
      state.shareStatus = payload;
    },
    setPendingLoginSave(state, { payload }: PayloadAction<IPendingLoginSave | null>) {
      state.pendingLoginSave = payload;
    }
  }
});

export const {
  resetNewSelection,
  hydrateNewSelection,
  addSelection,
  removeSelection,
  replaceSelection,
  handleLikes,
  addLikes,
  removeLikes,
  startSelection,
  finishSelection,
  finishPurchasedSelection,
  enableMobileSelectionMode,
  disableMobileSelectionMode,
  enableUpdateSelectionState,
  disableUpdateSelectionState,
  beginSelectionEdit,
  toggleSelectionEditImage,
  addSelectionEditImages,
  removeSelectionEditImages,
  openSelectionEditSaveModal,
  closeSelectionEditSaveModal,
  setSelectionEditStatus,
  completeSelectionEdit,
  discardSelectionEdit,
  setShareStatus,
  setPendingLoginSave
} = newSelectionSlice.actions;

export const newSelectionReducer = newSelectionSlice.reducer;
